import { useEffect, useRef } from "react";
import { useApiIsLoaded, useMapsLibrary } from "@vis.gl/react-google-maps";

interface AddressAutocompleteProps {
  onAddressSelect: (address: string, lat: number, lng: number) => void;
  /**
   * Fires whenever the user types or pastes text in the input, even if Google
   * never emits a `place_changed` (i.e. they wrote a custom address and never
   * selected from the suggestion list). Use this to keep a form field in sync
   * with the raw text — coordinates won't be available in that case, but at
   * least the address won't be lost.
   */
  onAddressTextChange?: (text: string) => void;
  placeholder?: string;
  className?: string;
  defaultValue?: string;
  value?: string;
}

export default function AddressAutocomplete({
  onAddressSelect,
  onAddressTextChange,
  placeholder = "Buscar dirección...",
  className = "",
  defaultValue = "",
  value,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const listenerRef = useRef<google.maps.MapsEventListener | null>(null);

  // Hooks de @vis.gl/react-google-maps para detección instantánea
  const apiIsLoaded = useApiIsLoaded();
  const placesLibrary = useMapsLibrary("places");

  // Sincronizar el valor del input cuando se proporciona externamente
  useEffect(() => {
    if (value !== undefined && inputRef.current && inputRef.current.value !== value) {
      inputRef.current.value = value;
    }
  }, [value]);

  // Inicializar Autocomplete cuando la librería places esté disponible
  useEffect(() => {
    if (!placesLibrary || !inputRef.current) return;

    // Evitar reinicializar si ya existe
    if (autocompleteRef.current) return;

    // Crear el autocompletar con restricción de país para acelerar búsquedas
    const autocomplete = new placesLibrary.Autocomplete(inputRef.current, {
      types: ["address"],
      fields: ["formatted_address", "geometry.location", "place_id"],
      componentRestrictions: { country: "mx" }, // Restringir a México
    });

    autocompleteRef.current = autocomplete;

    // Listener para cuando se selecciona una dirección
    listenerRef.current = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (!place.geometry || !place.geometry.location) {
        return;
      }

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address || "";

      onAddressSelect(address, lat, lng);
    });

    return () => {
      // Limpiar listener al desmontar
      if (listenerRef.current) {
        google.maps.event.removeListener(listenerRef.current);
        listenerRef.current = null;
      }
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [placesLibrary, onAddressSelect]);

  return (
    <input
      ref={inputRef}
      type="text"
      defaultValue={defaultValue}
      placeholder={apiIsLoaded ? placeholder : "Cargando..."}
      className={className}
      onChange={(e) => onAddressTextChange?.(e.target.value)}
      onBlur={(e) => onAddressTextChange?.(e.target.value)}
    />
  );
}
